import "reflect-metadata";

import "assets/css/reset.css";
import "assets/fonts/fonts.css";
import "assets/css/core.css";
import "assets/css/squadColor.css";

import React, {Suspense, Fragment} from "react";
import reportWebVitals from "./reportWebVitals";
import {RootRoutes, SecretGateRoutes} from "routes";
import {
	browserTracingIntegration,
	init,
	replayIntegration,
	breadcrumbsIntegration,
} from "@sentry/react";
import {BrowserRouter} from "react-router-dom";
import {createRoot} from "react-dom/client";
import {
	controllers,
	DIContainer,
	providers,
	services,
	stores,
} from "data/services/locator/locator_container.service";
import {InjectionProvider} from "data/services/locator/locator_provider.service";
import {StyledEngineProvider} from "@mui/material/styles";
import {ThemeProvider} from "@mui/material";
import {BASE_URL, SENTRY_DSN_URL} from "data/constants";
import {PagePreloader} from "views/components/preloader";
import {SecretGateController} from "views/controllers/secret_gate/secret_gate.controller";
import {Session} from "views/components/session/session.component";
import {Bootstrap} from "views/components/bootstrap/bootstrap.component";
import {Header} from "views/components/header/header.component";
import {theme} from "assets/theming/theme";
import {GameNavigation} from "views/components/game_navigation/game_navigation.component";
import type {ErrorEvent} from "@sentry/types/types/event";
import {PubNubHoc} from "views/components/pub_nub_hoc/pub_nub_hoc.component";
import {Modals} from "views/components/modals/modals";
import {HocFriendsRedirect} from "views/components/hoc_friends_redirect/hoc_friends_redirect.component";

if (SENTRY_DSN_URL) {
	let errorCount = 0;
	const MAX_ERRORS = 100;
	const IGNORE_ERRORS = [
		"Authentication Required",
		"Authorization failed",
		"Load failed",
		"UET is not defined",
		"Loading chunk",
		"AbortError: Share canceled",
		"captured as promise rejection",
		"reading 'insertBefore'",
		"Failed to fetch",
		"Network Error",
		"Minified Redux error",
		"Unexpected end of script",
		"Abort due to cancellation of share",
		"The network connection was lost.",
		"feature named `clickToLoad` was not found",
		"Maximum call stack size exceeded",
		"Possible side-effect in debug-evaluate",
		"Native bridge was not set",
		"Initialization Error: Native bridge was not set",
		"UnhandledRejection: Non-Error promise rejection captured with keys: message, status",
		"Non-Error promise rejection captured with keys: message, status",
		"TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
		"Identifier MlbJavascriptDataStore has already been declared",
		"Identifier 'MlbJavascriptDataStore' has already been declared",
		"SyntaxError: Identifier MlbJavascriptDataStore has already been declared",
		"SyntaxError: Identifier 'MlbJavascriptDataStore' has already been declared",
		"Java exception was raised during method invocation",
		"Object captured as promise rejection with keys: message, status",
		"UnhandledRejection: Object captured as promise rejection with keys: message, status",
	];

	init({
		dsn: SENTRY_DSN_URL,
		integrations: [
			browserTracingIntegration(),
			replayIntegration(),
			breadcrumbsIntegration({
				console: false,
			}),
		],
		environment: process.env.REACT_APP_SENTRY_ENV || "development",
		allowUrls: [".loc", ".fanhubmedia.com", ".geniussports.com", "www.mlb.com", ".mlb.com"],
		ignoreErrors: IGNORE_ERRORS,
		beforeSend: (event: ErrorEvent) => {
			errorCount++;
			if (errorCount > MAX_ERRORS) {
				return null;
			}
			return event;
		},
		denyUrls: [
			"quantcast",
			"xsca",
			// browser's extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^moz-extension:\/\//i,
		],
		sampleRate: 0.1,
	});
}

const root = document.getElementById("root");

if (!root) {
	throw Error("Root element was not found");
}

DIContainer.load(services, providers, stores, controllers);

createRoot(root).render(
	<>
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<InjectionProvider container={DIContainer}>
					<Bootstrap>
						<BrowserRouter basename={BASE_URL}>
							<Suspense fallback={<PagePreloader />}>
								{SecretGateController.IS_SECRET_PASSED ? (
									<Fragment>
										<Header />
										<GameNavigation />
										<Session>
											<HocFriendsRedirect>
												<PubNubHoc>
													<RootRoutes />
												</PubNubHoc>
											</HocFriendsRedirect>
										</Session>
										<Modals />
									</Fragment>
								) : (
									<SecretGateRoutes />
								)}
							</Suspense>
						</BrowserRouter>
					</Bootstrap>
				</InjectionProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	</>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
